// @ts-nocheck - untyped
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../../Context/AuthenticationContext';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { startOfDay, subDays } from 'date-fns';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ApiIcon from '@mui/icons-material/Api';
import Language from '@mui/icons-material/Language';
import CloudCircleOutlined from '@mui/icons-material/CloudCircleOutlined';
import PlaylistAddCheckCircleOutlined from '@mui/icons-material/PlaylistAddCheckCircleOutlined';
import AccountCircleOutlined from '@mui/icons-material/AccountCircleOutlined';
import VerifiedOutlined from '@mui/icons-material/VerifiedOutlined';

import ServicesMonitorService from '../../../Services/ServicesMonitorService';
import OverviewTab from './Overview/OverviewTab';
import ServiceTab from './ServiceDetails/ServiceTab';
import TimeRangeDialog from './Common/TimeRangeDialog';
import '../../../App.scss';
import './ServicesMonitor.scss';

function periodFromRange(start, end) {
  const hours = (end - start) / (1000 * 60 * 60);
  if (hours <= 1) return '1h';
  if (hours <= 12) return '12h';
  if (hours <= 24) return '24h';
  if (hours <= 24 * 7) return '7d';
  return '30d';
}

const canAccessMonitoring = roles => roles?.includes('ADMIN') || roles?.includes('USER_MANAGEMENT');

const ALL_SERVICES = [
  {
    id: 'csp',
    name: 'CSP',
    icon: ApiIcon,
    serviceParam: 'cspservice',
    hasMetrics: true,
    hasEndpointDrilldown: true,
    hasApiAnalytics: true,
  },
  {
    id: 'adv',
    name: 'ADV',
    icon: VerifiedOutlined,
    serviceParam: 'adv',
    hasMetrics: true,
    hasEndpointDrilldown: true,
    hasClientFilter: true,
    availableClients: [],
  },
  {
    id: 'webscraper',
    name: 'Web Scraper',
    icon: Language,
    serviceParam: 'webscraper-node',
    hasMetrics: true,
    hasEndpointDrilldown: true,
  },
  {
    id: 'ihub',
    name: 'iHub Adapter',
    icon: CloudCircleOutlined,
    serviceParam: 'ihubadapter',
    hasMetrics: true,
    hasEndpointDrilldown: true,
  },
  {
    id: 'bugnion',
    name: 'Bugnion',
    icon: PlaylistAddCheckCircleOutlined,
    serviceParam: 'bugnioninterface',
    hasMetrics: true,
    hasEndpointDrilldown: true,
  },
  {
    id: 'usermgmt',
    name: 'User Management',
    icon: AccountCircleOutlined,
    serviceParam: 'uaa',
    hasMetrics: true,
    hasEndpointDrilldown: true,
  },
];

const TAB_OVERVIEW = 0;
const getServiceTabIndex = i => 1 + i;

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && (
        <Box p={2} style={{ textAlign: 'left' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const createInitialServiceData = () => {
  const initial = {};
  ALL_SERVICES.forEach(service => {
    initial[service.id] = {
      health: null,
      healthLoading: false,
      healthError: null,
      metrics: null,
      metricsLoading: false,
      metricsError: null,
    };
  });
  return initial;
};

export default function ServicesMonitor() {
  const { user } = useAuth();

  const [tab, setTab] = useState(TAB_OVERVIEW);
  const [timeRangeStart, setTimeRangeStart] = useState(() => subDays(startOfDay(new Date()), 1));
  const [timeRangeEnd, setTimeRangeEnd] = useState(() => new Date());
  const [rangePickerOpen, setRangePickerOpen] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);
  const intervalRef = useRef(null);

  const [servicesData, setServicesData] = useState(createInitialServiceData);
  const [serviceEndpoints, setServiceEndpoints] = useState({});
  const [endpointsLoading, setEndpointsLoading] = useState({});
  const [selectedEndpoint, setSelectedEndpoint] = useState({});
  const selectedEndpointRef = useRef({});
  const [selectedClient, setSelectedClient] = useState({});
  const selectedClientRef = useRef({});
  const [endpointMetrics, setEndpointMetrics] = useState({});
  const [endpointMetricsLoading, setEndpointMetricsLoading] = useState({});
  const [advClientsList, setAdvClientsList] = useState([]);
  const [advClientsLoading, setAdvClientsLoading] = useState(false);

  const initialLoadRef = useRef(false);
  const tabRef = useRef(tab);
  tabRef.current = tab;
  const [metricsLoadedFor, setMetricsLoadedFor] = useState({});

  const hasAccess = canAccessMonitoring(user?.roles);

  const fetchServiceHealth = useCallback(async serviceId => {
    const service = ALL_SERVICES.find(s => s.id === serviceId);
    if (!service) return;

    setServicesData(prev => ({
      ...prev,
      [serviceId]: { ...prev[serviceId], healthLoading: true, healthError: null },
    }));

    try {
      const response = await ServicesMonitorService.getHealth(service.serviceParam);
      const data = response.data;
      setServicesData(prev => ({
        ...prev,
        [serviceId]: { ...prev[serviceId], health: data, healthLoading: false },
      }));
    } catch (err) {
      setServicesData(prev => ({
        ...prev,
        [serviceId]: {
          ...prev[serviceId],
          healthLoading: false,
          healthError: err.message || 'Failed to load health',
        },
      }));
    }
  }, []);

  const fetchServiceMetrics = useCallback(
    async serviceId => {
      const service = ALL_SERVICES.find(s => s.id === serviceId);
      if (!service || !service.hasMetrics) return;
      if (!timeRangeStart || !timeRangeEnd) return;

      setServicesData(prev => ({
        ...prev,
        [serviceId]: { ...prev[serviceId], metricsLoading: true, metricsError: null },
      }));

      try {
        const period = periodFromRange(timeRangeStart, timeRangeEnd);
        const fromISO = timeRangeStart.toISOString();
        const toISO = timeRangeEnd.toISOString();

        const response = await ServicesMonitorService.getHttpMetrics(
          period,
          service.serviceParam,
          fromISO,
          toISO
        );

        const data = response.data;

        setServicesData(prev => ({
          ...prev,
          [serviceId]: { ...prev[serviceId], metrics: data, metricsLoading: false },
        }));
        setLastUpdated(Date.now());
      } catch (err) {
        setServicesData(prev => ({
          ...prev,
          [serviceId]: {
            ...prev[serviceId],
            metricsLoading: false,
            metricsError: err.message || 'Failed to load metrics',
          },
        }));
      }
    },
    [timeRangeStart, timeRangeEnd]
  );

  const fetchServiceEndpoints = useCallback(async serviceId => {
    const service = ALL_SERVICES.find(s => s.id === serviceId);
    if (!service || !service.hasEndpointDrilldown) return;

    setEndpointsLoading(prev => ({ ...prev, [serviceId]: true }));

    try {
      const response = await ServicesMonitorService.getApiEndpoints(service.serviceParam);
      const list = response.data || [];
      setServiceEndpoints(prev => ({ ...prev, [serviceId]: list }));
    } catch (err) {
      console.error(`Failed to fetch ${serviceId} endpoints:`, err);
      setServiceEndpoints(prev => ({ ...prev, [serviceId]: [] }));
    } finally {
      setEndpointsLoading(prev => ({ ...prev, [serviceId]: false }));
    }
  }, []);

  const fetchEndpointMetrics = useCallback(
    async (serviceId, uri) => {
      if (!uri || !timeRangeStart || !timeRangeEnd) return;

      const service = ALL_SERVICES.find(s => s.id === serviceId);
      if (!service || !service.hasEndpointDrilldown) return;

      setEndpointMetricsLoading(prev => ({ ...prev, [serviceId]: true }));

      try {
        const period = periodFromRange(timeRangeStart, timeRangeEnd);
        const fromISO = timeRangeStart.toISOString();
        const toISO = timeRangeEnd.toISOString();
        const client = selectedClientRef.current[serviceId] || 'all';

        const response = await ServicesMonitorService.getHttpEndpointMetrics(
          uri,
          period,
          fromISO,
          toISO,
          service.serviceParam,
          client
        );

        const data = response.data;
        setEndpointMetrics(prev => ({ ...prev, [serviceId]: data }));
      } catch (err) {
        console.error(`Failed to fetch ${serviceId} endpoint metrics:`, err);
        setEndpointMetrics(prev => ({ ...prev, [serviceId]: null }));
      } finally {
        setEndpointMetricsLoading(prev => ({ ...prev, [serviceId]: false }));
      }
    },
    [timeRangeStart, timeRangeEnd]
  );

  const loadAllHealth = useCallback(async () => {
    const healthPromises = ALL_SERVICES.map(service => fetchServiceHealth(service.id));
    await Promise.allSettled(healthPromises);
  }, [fetchServiceHealth]);

  const loadServiceData = useCallback(
    async serviceId => {
      const service = ALL_SERVICES.find(s => s.id === serviceId);
      if (!service) return;

      const promises = [];
      if (service.hasMetrics) promises.push(fetchServiceMetrics(serviceId));
      if (service.hasEndpointDrilldown) promises.push(fetchServiceEndpoints(serviceId));
      await Promise.allSettled(promises);
      setMetricsLoadedFor(prev => ({ ...prev, [serviceId]: true }));
    },
    [fetchServiceMetrics, fetchServiceEndpoints]
  );

  useEffect(() => {
    if (!hasAccess) return;
    if (!initialLoadRef.current) {
      initialLoadRef.current = true;
      const timer = setTimeout(() => {
        loadAllHealth();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [hasAccess, loadAllHealth]);

  useEffect(() => {
    if (!hasAccess) return;
    if (!initialLoadRef.current) return;
    if (tab === TAB_OVERVIEW) return;

    const serviceIndex = tab - 1;
    const service = ALL_SERVICES[serviceIndex];
    if (service && !metricsLoadedFor[service.id]) {
      loadServiceData(service.id);
    }
  }, [hasAccess, tab, metricsLoadedFor, loadServiceData]);

  // Fetch available clients for ADV service (for client filter dropdown)
  useEffect(() => {
    const fetchAdvClients = async () => {
      setAdvClientsLoading(true);
      try {
        const period = periodFromRange(timeRangeStart, timeRangeEnd);
        const response = await ServicesMonitorService.getAvailableClients('adv', period);
        const clients = response.data || [];
        setAdvClientsList(clients);
      } catch (err) {
        console.warn('Failed to fetch available clients for ADV:', err);
        setAdvClientsList([]);
      } finally {
        setAdvClientsLoading(false);
      }
    };

    if (hasAccess && timeRangeStart && timeRangeEnd) {
      fetchAdvClients();
    }
  }, [hasAccess, timeRangeStart, timeRangeEnd]);

  const handleRefresh = useCallback(async () => {
    if (tab === TAB_OVERVIEW) {
      await loadAllHealth();
    } else {
      const serviceIndex = tab - 1;
      const service = ALL_SERVICES[serviceIndex];
      if (service) {
        const promises = [fetchServiceHealth(service.id), loadServiceData(service.id)];
        const endpointUri = selectedEndpointRef.current[service.id];
        if (endpointUri) {
          promises.push(fetchEndpointMetrics(service.id, endpointUri));
        }
        await Promise.allSettled(promises);
      }
    }
  }, [tab, loadAllHealth, fetchServiceHealth, loadServiceData, fetchEndpointMetrics]);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (refreshInterval > 0) {
      intervalRef.current = setInterval(handleRefresh, refreshInterval * 1000);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [refreshInterval, handleRefresh]);

  useEffect(() => {
    if (!hasAccess) return;
    if (!initialLoadRef.current) return;

    setMetricsLoadedFor({});

    const currentTab = tabRef.current;
    if (currentTab === TAB_OVERVIEW) return;

    const serviceIndex = currentTab - 1;
    const service = ALL_SERVICES[serviceIndex];
    if (!service) return;

    const reloadMetrics = async () => {
      const promises = [];
      if (service.hasMetrics) promises.push(fetchServiceMetrics(service.id));
      const endpointUri = selectedEndpointRef.current[service.id];
      if (service.hasEndpointDrilldown && endpointUri) {
        promises.push(fetchEndpointMetrics(service.id, endpointUri));
      }
      await Promise.allSettled(promises);
      setMetricsLoadedFor(prev => ({ ...prev, [service.id]: true }));
    };

    reloadMetrics();
  }, [hasAccess, timeRangeStart, timeRangeEnd, fetchServiceMetrics, fetchEndpointMetrics]);

  const handleEndpointChange = useCallback(
    (serviceId, uri) => {
      selectedEndpointRef.current = { ...selectedEndpointRef.current, [serviceId]: uri };
      setSelectedEndpoint(prev => ({ ...prev, [serviceId]: uri }));

      if (uri) {
        fetchEndpointMetrics(serviceId, uri);
      } else {
        setEndpointMetrics(prev => ({ ...prev, [serviceId]: null }));
      }
    },
    [fetchEndpointMetrics]
  );

  const handleClientChange = useCallback(
    (serviceId, client) => {
      const normalizedClient = `${client || ''}`.trim().toLowerCase() || 'all';
      selectedClientRef.current = { ...selectedClientRef.current, [serviceId]: normalizedClient };
      setSelectedClient(prev => ({ ...prev, [serviceId]: normalizedClient }));
      const uri = selectedEndpoint[serviceId];
      if (uri) {
        fetchEndpointMetrics(serviceId, uri);
      }
    },
    [selectedEndpoint, fetchEndpointMetrics]
  );

  const handleApplyTimeRange = (start, end) => {
    setTimeRangeStart(start);
    setTimeRangeEnd(end);
  };

  if (!hasAccess) {
    return <Navigate to="/unauthorized" replace />;
  }

  const overviewLoading = Object.values(servicesData).some(d => d.healthLoading);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div className="sm-container" id="services-monitor-container">
        <div className="sm-tab-wrapper">
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            id="services-monitor-tabs"
            aria-label="Services monitor navigation"
          >
            <Tab
              icon={<DashboardIcon fontSize="small" />}
              iconPosition="start"
              label="Overview"
              className={tab === TAB_OVERVIEW ? 'sm-tab-selected' : 'sm-tab-unselected'}
              id="services-monitor-overview-tab"
              aria-controls="services-monitor-overview-panel"
            />

            {ALL_SERVICES.map((service, idx) => (
              <Tab
                key={service.id}
                icon={<service.icon fontSize="small" />}
                iconPosition="start"
                label={service.name}
                className={
                  tab === getServiceTabIndex(idx) ? 'sm-tab-selected' : 'sm-tab-unselected'
                }
                id={`services-monitor-${service.id}-tab`}
                aria-controls={`services-monitor-${service.id}-panel`}
              />
            ))}
          </Tabs>
        </div>

        <TabPanel value={tab} index={TAB_OVERVIEW}>
          <OverviewTab
            services={ALL_SERVICES}
            servicesData={servicesData}
            onServiceClick={idx => setTab(getServiceTabIndex(idx))}
            onRefresh={handleRefresh}
            loading={overviewLoading}
          />
        </TabPanel>

        {ALL_SERVICES.map((service, idx) => {
          const data = servicesData[service.id] || {};
          const serviceLoading = data.healthLoading || data.metricsLoading;

          return (
            <TabPanel key={service.id} value={tab} index={getServiceTabIndex(idx)}>
              <ServiceTab
                service={service}
                timeRangeStart={timeRangeStart}
                timeRangeEnd={timeRangeEnd}
                onOpenRangePicker={() => setRangePickerOpen(true)}
                refreshInterval={refreshInterval}
                onRefreshIntervalChange={setRefreshInterval}
                lastUpdated={lastUpdated}
                onRefresh={handleRefresh}
                loading={serviceLoading}
                health={data.health}
                healthLoading={data.healthLoading}
                healthError={data.healthError}
                onHealthRetry={() => fetchServiceHealth(service.id)}
                metrics={data.metrics}
                metricsLoading={data.metricsLoading}
                metricsError={data.metricsError}
                onMetricsRetry={() => fetchServiceMetrics(service.id)}
                hasEndpointDrilldown={service.hasEndpointDrilldown}
                endpointUri={selectedEndpoint[service.id] || ''}
                onEndpointChange={uri => handleEndpointChange(service.id, uri)}
                endpointList={serviceEndpoints[service.id] || []}
                endpointListLoading={endpointsLoading[service.id] || false}
                endpointData={endpointMetrics[service.id]}
                endpointLoading={endpointMetricsLoading[service.id] || false}
                hasClientFilter={service.hasClientFilter || false}
                availableClients={
                  service.id === 'adv' ? advClientsList : service.availableClients || []
                }
                clientsLoading={service.id === 'adv' ? advClientsLoading : false}
                clientFilter={selectedClient[service.id] || 'all'}
                onClientFilterChange={client => handleClientChange(service.id, client)}
                hasApiAnalytics={service.hasApiAnalytics || false}
              />
            </TabPanel>
          );
        })}

        <TimeRangeDialog
          open={rangePickerOpen}
          onClose={() => setRangePickerOpen(false)}
          initialStart={timeRangeStart}
          initialEnd={timeRangeEnd}
          onApply={handleApplyTimeRange}
        />
      </div>
    </LocalizationProvider>
  );
}
